import React, { useState } from "react";
import { toast } from "react-toastify";
import { Copy } from "lucide-react";
import { SecondaryButton } from "../../UI";
import ConfirmModal from "../../UI/Modal/confirmModal";
import { rotateProductFeedToken, setProductFeedEnabled } from "../../../utils/APIs";
import { Field, IconButton, InfoTip, NumberInput, Section, SelectInput, TextInput, Toggle, WithInfo } from "./common";
import { INTERVAL_LABELS } from "./constants";
import DeliverySection from "./DeliverySection";
import { GLOSSARY } from "./glossary";
import { errorMessage, formatDate, setIn } from "./helpers";

// Schedule & delivery tab. Settings here are part of the draft; the pause
// switch (and the feed URL, when CSV delivery is on) apply immediately.
const ScheduleTab = ({ feed, draft, updateDraft, auth, feedId, meta, onFeedChange, reload }) => {
    const [confirmRotate, setConfirmRotate] = useState(false);
    const [busy, setBusy] = useState(false);
    const settings = draft?.settings || {};
    const setSetting = (path, value) => updateDraft((d) => setIn(d, ["settings", ...path], value));
    // The live feed follows the published config; the settings below are the draft's.
    const publishedApi = feed.delivery_method === "api";
    const draftApi = settings.delivery?.method === "api";
    // Feed URL delivery is off unless the server enables it.
    const csvEnabled = !!meta?.csv_delivery_enabled;

    const copyUrl = () => {
        navigator.clipboard.writeText(feed.public_url);
        toast.success("Feed URL copied");
    };

    const rotate = async () => {
        try {
            setBusy(true);
            const result = await rotateProductFeedToken({ ...auth, feedId });
            onFeedChange(result?.data);
            setConfirmRotate(false);
            toast.success(result?.message);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const toggleEnabled = async (enabled) => {
        try {
            setBusy(true);
            const result = await setProductFeedEnabled({ ...auth, feedId, enabled });
            onFeedChange(result?.data);
            toast.success(result?.message);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col gap-4 mt-4 max-w-4xl">
            {publishedApi || !csvEnabled ? (
                <Section
                    title="Feed status"
                    description={publishedApi
                        ? "This feed is delivered through the Adobe Target API, so its URL is switched off."
                        : "Feed URL delivery is coming soon. Until then feeds are delivered through the Adobe Target API."}
                >
                    <div className="flex flex-wrap items-center gap-6 text-xs text-gray-500">
                        <WithInfo tip={GLOSSARY.feedStatus.lastRun}>
                            Last run: {feed.last_run ? `${feed.last_run.outcome}, ${feed.last_run.live_rows ?? "—"} products, ${formatDate(feed.last_run.finished_at)}` : "none yet"}
                        </WithInfo>
                        {feed.next_run_at && <span>Next build: {formatDate(feed.next_run_at)}</span>}
                    </div>
                    <Toggle
                        checked={feed.enabled}
                        onChange={toggleEnabled}
                        label={feed.enabled ? "Feed active" : "Feed paused"}
                        help="A paused feed isn't rebuilt or pushed. Target keeps the products it has until they expire."
                        tip={GLOSSARY.schedule.enabled}
                    />
                </Section>
            ) : (
                <Section title="Feed URL" description="Register this URL in Adobe Target (Recommendations → Feeds → URL). Anyone with the URL can read the file." tip={GLOSSARY.schedule.url}>
                    <div className="flex items-center gap-2">
                        <TextInput monospace disabled value={feed.public_url || ""} />
                        <IconButton title="Copy" onClick={copyUrl}><Copy size={16} /></IconButton>
                        <SecondaryButton size="sm" disable={busy} handleClick={() => setConfirmRotate(true)}>Rotate</SecondaryButton>
                        <InfoTip content={GLOSSARY.schedule.rotate} />
                    </div>
                    <div className="flex flex-wrap items-center gap-6 text-xs text-gray-500">
                        <WithInfo tip={GLOSSARY.feedStatus.lastRun}>
                            Last run: {feed.last_run ? `${feed.last_run.outcome}, ${feed.last_run.live_rows ?? "—"} products, ${formatDate(feed.last_run.finished_at)}` : "none yet"}
                        </WithInfo>
                        <span>Last Target pull: {formatDate(feed.last_pulled_at)}{feed.last_pulled_user_agent ? ` (${feed.last_pulled_user_agent})` : ""}</span>
                        {feed.next_run_at && <span>Next build: {formatDate(feed.next_run_at)}</span>}
                    </div>
                    <Toggle
                        checked={feed.enabled}
                        onChange={toggleEnabled}
                        label={feed.enabled ? "Feed active" : "Feed paused"}
                        help="A paused feed isn't rebuilt and its URL returns 404, so Target's import fails until you resume it."
                        tip={GLOSSARY.schedule.enabled}
                    />
                </Section>
            )}

            {draft && <DeliverySection feed={feed} settings={settings} setSetting={setSetting} auth={auth} csvEnabled={csvEnabled} reload={reload} />}

            {draft && (
                <>
                    <Section
                        title="Schedule"
                        description={
                            draftApi
                                ? "Builds run every 12 hours at most and every two weeks at least. Each successful build pushes every product to Target straight away."
                                : "Builds run every 12 hours at most and every two weeks at least. Target's URL feed imports run daily, weekly or every two weeks, so build at least as often as Target imports."
                        }
                    >
                        <Field label="Build" tip={GLOSSARY.schedule.interval}>
                            <SelectInput
                                className="max-w-[260px]"
                                value={settings.schedule?.interval || "24h"}
                                options={(meta?.schedule_intervals || Object.keys(INTERVAL_LABELS)).map((v) => ({ value: v, label: INTERVAL_LABELS[v] || v }))}
                                onChange={(interval) => setSetting(["schedule", "interval"], interval)}
                            />
                        </Field>
                    </Section>

                    <Section title="Guardrails" description="A build that breaks a guardrail is held: nothing is sent, Target keeps what it has and alert recipients are emailed." tip={GLOSSARY.stats.guardrails}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Field label="Minimum products" tip={GLOSSARY.schedule.minProducts}>
                                <NumberInput value={settings.guardrails?.min_products} min={0} onChange={(v) => setSetting(["guardrails", "min_products"], v)} />
                            </Field>
                            <Field label="Maximum drop vs. published (%)" tip={GLOSSARY.schedule.maxDrop}>
                                <NumberInput value={settings.guardrails?.max_drop_pct} min={0} max={100} onChange={(v) => setSetting(["guardrails", "max_drop_pct"], v)} />
                            </Field>
                            <Field label="Maximum invalid rows (%)" tip={GLOSSARY.schedule.maxInvalid}>
                                <NumberInput value={settings.guardrails?.max_invalid_rows_pct} min={0} max={100} onChange={(v) => setSetting(["guardrails", "max_invalid_rows_pct"], v)} />
                            </Field>
                        </div>
                    </Section>

                    <Section title="File">
                        <Toggle checked={settings.csv?.id_strict !== false} onChange={(v) => setSetting(["csv", "id_strict"], v)} label="Strict entity ids" help="Drop rows whose id has characters Target doesn't allow. When off, those characters become '-', which may no longer match the ids your site sends." tip={GLOSSARY.schedule.strictIds} />
                        <Toggle checked={!!settings.csv?.bom} onChange={(v) => setSetting(["csv", "bom"], v)} label="Write a UTF-8 BOM" help="Leave off unless Target shows garbled characters." tip={GLOSSARY.schedule.bom} />
                    </Section>

                    <Section title="Alerts">
                        <Field label="Email when a build is held or fails" help="Comma-separated addresses" tip={GLOSSARY.schedule.alerts}>
                            <TextInput
                                value={(settings.alerts?.recipients || []).join(", ")}
                                onChange={(text) => setSetting(["alerts", "recipients"], text.split(",").map((v) => v.trim()).filter(Boolean))}
                            />
                        </Field>
                    </Section>
                </>
            )}

            <Section title="Adobe Target checklist">
                {draftApi ? (
                    <ol className="list-decimal pl-5 text-sm text-gray-700 flex flex-col gap-1">
                        <li>Under Setup → Adobe IO, make sure the integration has Adobe Target enabled, a tenant name, and a product profile that can edit Target (Editor or Approver).</li>
                        <li>Choose the connection and environment above, then publish. Every build pushes every product; the Runs tab shows the push result.</li>
                        <li>If Target has a URL feed for this feed, delete or deactivate it so the two don't overwrite each other.</li>
                        <li>Check a few products in Target under Recommendations → Catalog Search, in the environment you chose.</li>
                        <li>Make sure your site sends the same entity.id values as the feed's key field on product pages.</li>
                        <li>Add the inclusion rule <b>inventory &gt; 0</b> to your criteria so unavailable products aren't recommended.</li>
                        <li>Use the custom attributes (lists, numbered columns or variants JSON) in your designs.</li>
                    </ol>
                ) : (
                    <ol className="list-decimal pl-5 text-sm text-gray-700 flex flex-col gap-1">
                        <li>Publish the feed and check the Runs tab shows a published build.</li>
                        <li>In Target, go to Recommendations → Feeds → Create Feed, choose <b>URL</b> and paste the feed URL. No login is needed.</li>
                        <li>Choose the schedule, environment (host group) and let Target import it once. The status should read "Feed Imported Successfully".</li>
                        <li>Make sure your site sends the same entity.id values as the feed's key field on product pages.</li>
                        <li>Add the inclusion rule <b>inventory &gt; 0</b> to your criteria so unavailable products aren't recommended.</li>
                        <li>Use the custom attributes (lists, numbered columns or variants JSON) in your designs.</li>
                    </ol>
                )}
            </Section>

            {confirmRotate && (
                <ConfirmModal
                    title="Rotate feed URL"
                    ConfirmText="Rotate"
                    description="The current URL stops working immediately. Update the feed URL in Adobe Target afterwards, or its next import fails."
                    hideModal={() => setConfirmRotate(false)}
                    handleConfirmation={rotate}
                    loading={busy}
                />
            )}
        </div>
    );
};

export default ScheduleTab;
